import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Thermometer, Scale, User, AlertTriangle, CheckCircle, Clock, Package, ChevronDown, ChevronUp, Play, Pause, Square, Plus, X } from 'lucide-react';
import type { Order } from '../types/Order';
import type { InspectionRecord } from '../types/InspectionRecord';
import { formatInspectionTime, getInspectionStatus } from '../types/InspectionRecord';
interface OrderDetailViewProps {
  order: Order;
  inspections: InspectionRecord[];
  onBack: () => void;
  onContinue?: () => void;
  onPause?: () => void;
  onComplete?: () => void;
  onAddSample?: () => void;
}
interface SampleGroup {
  sampleNumber: number;
  inspections: InspectionRecord[];
}
export function OrderDetailView({
  order,
  inspections,
  onBack,
  onContinue,
  onPause,
  onComplete,
  onAddSample
}: OrderDetailViewProps) {
  // Group inspections into samples of 5
  const samples: SampleGroup[] = [];
  for (let i = 0; i < inspections.length; i += 5) {
    samples.push({
      sampleNumber: Math.floor(i / 5) + 1,
      inspections: inspections.slice(i, i + 5)
    });
  }
  const [expandedSamples, setExpandedSamples] = useState<Set<number>>(new Set([1]));
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
  const [showPauseConfirm, setShowPauseConfirm] = useState(false);
  const toggleSample = (sampleNumber: number) => {
    const newExpanded = new Set(expandedSamples);
    if (newExpanded.has(sampleNumber)) {
      newExpanded.delete(sampleNumber);
    } else {
      newExpanded.add(sampleNumber);
    }
    setExpandedSamples(newExpanded);
  };
  const handleCompleteClick = () => {
    setShowCompleteConfirm(true);
  };
  const handleConfirmComplete = () => {
    setShowCompleteConfirm(false);
    if (onComplete) {
      onComplete();
    }
  };
  const handleCancelComplete = () => {
    setShowCompleteConfirm(false);
  };
  const handlePauseClick = () => {
    setShowPauseConfirm(true);
  };
  const handleConfirmPause = () => {
    setShowPauseConfirm(false);
    if (onPause) {
      onPause();
    }
  };
  const handleCancelPause = () => {
    setShowPauseConfirm(false);
  };
  const canContinue = order.status === 'paused';
  const canPause = order.status === 'active';
  const canComplete = order.status === 'active' || order.status === 'paused';
  const canAddSample = order.status === 'active';
  return <div className="min-h-screen w-full bg-white" data-id="element-462">
      <div className="max-w-7xl mx-auto px-8 py-8" data-id="element-463">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4
      }} data-id="element-464">
          {/* Compact Header with Actions */}
          <div className="flex items-center justify-between mb-6" data-id="element-465">
            <div className="flex items-center gap-4" data-id="element-466">
              <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" aria-label="Zurück" data-id="element-467">
                <ArrowLeft className="w-5 h-5 text-gray-600" strokeWidth={2} data-id="element-468" />
              </button>
              <div data-id="element-469">
                <h1 className="text-3xl font-semibold text-gray-900" data-id="element-470">
                  {order.produktName}
                </h1>
                <p className="text-sm text-gray-500 mt-0.5" data-id="element-471">
                  Charge: {order.chargenNummer} • Linie: {order.linie} •
                  Produkt-Nr: {order.produktNr}
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2" data-id="element-472">
              {canAddSample && onAddSample && <button onClick={onAddSample} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors" data-id="element-473">
                  <Plus className="w-4 h-4" strokeWidth={2} data-id="element-474" />
                  Weitere Stichprobe erfassen
                </button>}

              {canContinue && onContinue && <button onClick={onContinue} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors" data-id="element-475">
                  <Play className="w-4 h-4" strokeWidth={2} data-id="element-476" />
                  Fortsetzen
                </button>}

              {canPause && onPause && <button onClick={handlePauseClick} className="flex items-center gap-2 px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg font-medium transition-colors" data-id="element-477">
                  <Pause className="w-4 h-4" strokeWidth={2} data-id="element-478" />
                  Pausieren
                </button>}

              {canComplete && onComplete && <button onClick={handleCompleteClick} className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors" data-id="element-479">
                  <Square className="w-4 h-4" strokeWidth={2} data-id="element-480" />
                  Beenden
                </button>}
            </div>
          </div>

          {/* Compact Summary Cards */}
          <div className="grid grid-cols-4 gap-3 mb-6" data-id="element-481">
            <div className="bg-blue-50 rounded-lg p-3 border border-blue-100" data-id="element-482">
              <div className="flex items-center gap-2 mb-1" data-id="element-483">
                <Package className="w-4 h-4 text-blue-600" strokeWidth={2} data-id="element-484" />
                <span className="text-xs font-medium text-blue-700" data-id="element-485">
                  Prüflinge
                </span>
              </div>
              <p className="text-2xl font-bold text-blue-900" data-id="element-486">
                {inspections.length}
              </p>
            </div>

            <div className="bg-orange-50 rounded-lg p-3 border border-orange-100" data-id="element-487">
              <div className="flex items-center gap-2 mb-1" data-id="element-488">
                <Thermometer className="w-4 h-4 text-orange-600" strokeWidth={2} data-id="element-489" />
                <span className="text-xs font-medium text-orange-700" data-id="element-490">
                  Ø Temp
                </span>
              </div>
              <p className="text-2xl font-bold text-orange-900" data-id="element-491">
                {order.avgTemperature.toFixed(1)} °C
              </p>
            </div>

            <div className="bg-green-50 rounded-lg p-3 border border-green-100" data-id="element-492">
              <div className="flex items-center gap-2 mb-1" data-id="element-493">
                <Scale className="w-4 h-4 text-green-600" strokeWidth={2} data-id="element-494" />
                <span className="text-xs font-medium text-green-700" data-id="element-495">
                  Ø Gewicht
                </span>
              </div>
              <p className="text-2xl font-bold text-green-900" data-id="element-496">
                {order.avgWeight.toFixed(2)} kg
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200" data-id="element-497">
              <div className="flex items-center gap-2 mb-1" data-id="element-498">
                <Clock className="w-4 h-4 text-gray-600" strokeWidth={2} data-id="element-499" />
                <span className="text-xs font-medium text-gray-700" data-id="element-500">
                  Intervall
                </span>
              </div>
              <p className="text-2xl font-bold text-gray-900" data-id="element-501">30 min</p>
            </div>
          </div>

          {/* Samples List */}
          <div className="space-y-3" data-id="element-502">
            <h2 className="text-xl font-semibold text-gray-900" data-id="element-503">
              Stichproben ({samples.length})
            </h2>

            {samples.length === 0 ? <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200" data-id="element-504">
                <p className="text-gray-500" data-id="element-505">Noch keine Prüfungen erfasst</p>
              </div> : <div className="space-y-2" data-id="element-506">
                {samples.map(sample => {
              const isExpanded = expandedSamples.has(sample.sampleNumber);
              const hasWarnings = sample.inspections.some(i => i.fehlerMeldung);
              return <div key={sample.sampleNumber} className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-507">
                      {/* Sample Header */}
                      <button onClick={() => toggleSample(sample.sampleNumber)} className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors" data-id="element-508">
                        <div className="flex items-center gap-3" data-id="element-509">
                          <div className="flex items-center gap-2" data-id="element-510">
                            <Package className="w-5 h-5 text-gray-600" strokeWidth={2} data-id="element-511" />
                            <span className="font-semibold text-gray-900" data-id="element-512">
                              Stichprobe {sample.sampleNumber}
                            </span>
                          </div>
                          <span className="text-sm text-gray-500" data-id="element-513">
                            {sample.inspections.length} Prüfungen
                          </span>
                          {hasWarnings && <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs font-medium rounded-full" data-id="element-514">
                              Quittierungen
                            </span>}
                        </div>
                        {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" strokeWidth={2} data-id="element-515" /> : <ChevronDown className="w-5 h-5 text-gray-400" strokeWidth={2} data-id="element-516" />}
                      </button>

                      {/* Sample Content */}
                      <AnimatePresence data-id="element-517">
                        {isExpanded && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.2
                  }} className="overflow-hidden" data-id="element-518">
                            <div className="p-4 space-y-2 bg-white" data-id="element-519">
                              {sample.inspections.map((record, idx) => {
                        const status = getInspectionStatus(record);
                        const statusColors = {
                          green: {
                            bg: 'bg-green-50',
                            border: 'border-green-200',
                            badge: 'bg-green-100 text-green-700'
                          },
                          yellow: {
                            bg: 'bg-yellow-50',
                            border: 'border-yellow-200',
                            badge: 'bg-yellow-100 text-yellow-700'
                          },
                          red: {
                            bg: 'bg-red-50',
                            border: 'border-red-200',
                            badge: 'bg-red-100 text-red-700'
                          }
                        };
                        const colors = statusColors[status.color as keyof typeof statusColors];
                        return <div key={record.id} className={`${colors.bg} rounded-lg border ${colors.border} p-3`} data-id="element-520">
                                    {/* Compact Header */}
                                    <div className="flex items-center justify-between mb-2" data-id="element-521">
                                      <div className="flex items-center gap-2" data-id="element-522">
                                        <span className="font-semibold text-gray-900 text-sm" data-id="element-523">
                                          Prüfung {idx + 1}
                                        </span>
                                        <span className="text-xs text-gray-500" data-id="element-524">
                                          {formatInspectionTime(record.datum)}
                                        </span>
                                      </div>
                                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors.badge}`} data-id="element-525">
                                        {status.label}
                                      </span>
                                    </div>

                                    {/* Compact Data Grid */}
                                    <div className="grid grid-cols-4 gap-3 text-sm mb-2" data-id="element-526">
                                      <div data-id="element-527">
                                        <p className="text-xs text-gray-500" data-id="element-528">
                                          Temperatur
                                        </p>
                                        <p className="font-semibold text-gray-900" data-id="element-529">
                                          {record.temperatur.toFixed(1)} °C
                                        </p>
                                      </div>
                                      <div data-id="element-530">
                                        <p className="text-xs text-gray-500" data-id="element-531">
                                          Gewicht
                                        </p>
                                        <p className="font-semibold text-gray-900" data-id="element-532">
                                          {record.gewicht.toFixed(2)} kg
                                        </p>
                                      </div>
                                      <div data-id="element-533">
                                        <p className="text-xs text-gray-500" data-id="element-534">
                                          Personal
                                        </p>
                                        <p className="font-medium text-gray-900 text-xs" data-id="element-535">
                                          {record.personalName}
                                        </p>
                                        <p className="text-xs text-gray-500" data-id="element-536">
                                          Nr: {record.personalNummer}
                                        </p>
                                      </div>
                                      <div data-id="element-537">
                                        <p className="text-xs text-gray-500" data-id="element-538">
                                          Ref-Temp
                                        </p>
                                        <p className="font-medium text-gray-900" data-id="element-539">
                                          {record.referenztemperatur}
                                        </p>
                                      </div>
                                    </div>

                                    {/* Chip Number if present */}
                                    {record.chipNr && <div className="mt-2 p-2 bg-white rounded border border-gray-200" data-id="element-540">
                                        <p className="text-xs" data-id="element-541">
                                          <span className="text-gray-500" data-id="element-542">
                                            Supervisor Chip:
                                          </span>
                                          <span className="ml-2 font-semibold text-gray-900" data-id="element-543">
                                            {record.chipNr}
                                          </span>
                                        </p>
                                      </div>}

                                    {/* Error Message */}
                                    {record.fehlerMeldung && <div className="mt-2 p-2 bg-white rounded border-2 border-yellow-300" data-id="element-544">
                                        <div className="flex items-start gap-2" data-id="element-545">
                                          <AlertTriangle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" strokeWidth={2} data-id="element-546" />
                                          <p className="text-xs text-gray-700" data-id="element-547">
                                            {record.fehlerMeldung}
                                          </p>
                                        </div>
                                      </div>}
                                  </div>;
                      })}
                            </div>
                          </motion.div>}
                      </AnimatePresence>
                    </div>;
            })}
              </div>}
          </div>
        </motion.div>
      </div>

      {/* Complete Confirmation Modal */}
      <AnimatePresence data-id="element-548">
        {showCompleteConfirm && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-black/50 z-[60]" onClick={handleCancelComplete} data-id="element-549" />
            <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-id="element-550">
              <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} transition={{
            duration: 0.2
          }} className="w-full max-w-md" data-id="element-551">
                <div className="bg-white rounded-2xl shadow-2xl overflow-hidden" data-id="element-552">
                  <div className="p-6" data-id="element-553">
                    <div className="flex justify-between items-start mb-4" data-id="element-554">
                      <h3 className="text-2xl font-semibold text-gray-900" data-id="element-555">
                        Prüfung beenden?
                      </h3>
                      <button onClick={handleCancelComplete} className="p-1 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-556">
                        <X className="w-5 h-5 text-gray-500" strokeWidth={2} data-id="element-557" />
                      </button>
                    </div>

                    <p className="text-gray-600 mb-6" data-id="element-558">
                      Wollen Sie die Prüfung wirklich beenden?
                    </p>

                    <div className="flex gap-3" data-id="element-559">
                      <button onClick={handleCancelComplete} className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors" data-id="element-560">
                        Nein
                      </button>
                      <button onClick={handleConfirmComplete} className="flex-1 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors" data-id="element-561">
                        Beenden
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </>}
      </AnimatePresence>

      {/* Pause Confirmation Modal */}
      <AnimatePresence data-id="element-562">
        {showPauseConfirm && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-black/50 z-[60]" onClick={handleCancelPause} data-id="element-563" />
            <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-id="element-564">
              <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} transition={{
            duration: 0.2
          }} className="w-full max-w-md" data-id="element-565">
                <div className="bg-white rounded-2xl shadow-2xl overflow-hidden" data-id="element-566">
                  <div className="p-6" data-id="element-567">
                    <div className="flex justify-between items-start mb-4" data-id="element-568">
                      <h3 className="text-2xl font-semibold text-gray-900" data-id="element-569">
                        Prüfung pausieren?
                      </h3>
                      <button onClick={handleCancelPause} className="p-1 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-570">
                        <X className="w-5 h-5 text-gray-500" strokeWidth={2} data-id="element-571" />
                      </button>
                    </div>

                    <p className="text-gray-600 mb-6" data-id="element-572">
                      Wollen Sie die Prüfung wirklich pausieren?
                    </p>

                    <div className="flex gap-3" data-id="element-573">
                      <button onClick={handleCancelPause} className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors" data-id="element-574">
                        Nein
                      </button>
                      <button onClick={handleConfirmPause} className="flex-1 px-4 py-3 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg font-medium transition-colors" data-id="element-575">
                        Pausieren
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </>}
      </AnimatePresence>
    </div>;
}